import { useEffect } from "react";
import {
  initBiff,
  refreshEmptyAdSlots,
  waitForCookieConsent,
  type ConsentDiagnostics,
} from "@/lib/AdManagment/biffUtils";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { EnvConfig } from "@/lib/Helpers/getServerConfig";

interface UseBiffInitializationProps {
  hasBannerGPT: boolean;
  isLoggedIn: boolean;
  isProduction: boolean;
  envConfig?: EnvConfig | null;
}

// BIFF's config API (cdn.biff.travel, behind Cloudflare) intermittently rejects
// the request (403 without CORS headers), and the BIFF library does not retry.
// A single failure otherwise leaves the whole page view with zero ads, so we
// retry the init with exponential backoff.
const MAX_BIFF_ATTEMPTS = 4;
const BIFF_RETRY_DELAYS_MS = [1000, 3000, 9000]; // delay before attempts 2, 3, 4

// After init completes, give BIFF's initial display() time to render, then
// re-render any slot that's still empty (covers the landing page, which never
// re-mounts and so never gets the per-AdSlot refresh).
const LANDING_REFRESH_DELAY_MS = 2000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Temporary instrumentation: log every informative consent-gate resolution and
// a small sample of the boring happy path, so we can later decide whether the
// poll fallback / legacy oil_GTM_cookie can be removed. Always log the cases
// that inform that decision; sample the rest to keep volume down.
const CONSENT_DIAGNOSTICS_SAMPLE_RATE = 0.05;

const shouldLogConsentDiagnostics = (d: ConsentDiagnostics): boolean => {
  if (d.outcome !== "init") return true; // denied / timeout
  if (d.resolvedBy === "poll") return true; // a poll tick was the deciding path
  if (d.consentSource === "group") return true; // group signal was the only consent source
  return Math.random() < CONSENT_DIAGNOSTICS_SAMPLE_RATE;
};

export const useBiffInitialization = ({
  hasBannerGPT,
  isLoggedIn,
  isProduction,
  envConfig,
}: UseBiffInitializationProps) => {
  useEffect(() => {
    if (!hasBannerGPT || !isProduction) return;

    // Already initialized, or a run is already in flight (StrictMode
    // double-invoke / remount) — don't kick off a duplicate.
    if (window.globalBiffInitialized || window.globalBiffInitializing) return;

    window.globalBiffInitializing = true;
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const initializeGlobalBiff = async () => {
      try {
        const consentResult = await waitForCookieConsent();

        if (envConfig && shouldLogConsentDiagnostics(consentResult.diagnostics)) {
          void sendKibanaLog({
            envConfig,
            message: "BIFF consent gate resolved",
            category: "ad",
            component: "biff-consent",
            jsonMessage: consentResult.diagnostics,
          });
        }

        if (cancelled || !consentResult.shouldInitializeBiff) return;

        for (
          let attempt = 1;
          attempt <= MAX_BIFF_ATTEMPTS && !cancelled;
          attempt++
        ) {
          try {
            await initBiff(isProduction, isLoggedIn);
            window.globalBiffInitialized = true;
            // Landing-page safety net: re-render any slot still empty after
            // BIFF's initial display() has had time to run.
            refreshTimer = setTimeout(() => {
              if (!cancelled) refreshEmptyAdSlots();
            }, LANDING_REFRESH_DELAY_MS);
            return;
          } catch (error) {
            console.error(
              `BIFF init attempt ${attempt}/${MAX_BIFF_ATTEMPTS} failed:`,
              error
            );
            if (attempt < MAX_BIFF_ATTEMPTS && !cancelled) {
              await delay(BIFF_RETRY_DELAYS_MS[attempt - 1]);
            }
          }
        }
      } finally {
        // Release the lock so a later remount can retry if we never succeeded.
        window.globalBiffInitializing = false;
      }
    };

    initializeGlobalBiff();

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [hasBannerGPT, isLoggedIn, isProduction, envConfig]);
};

export default useBiffInitialization;
